import { memo } from 'react';
import { parseDate } from '@internationalized/date';
import { useFormContext, useWatch } from 'react-hook-form';
import { TmdxDatepicker } from '@ds';
import { AdminFormRow, ConditionalRender } from '@ui';
import { EmployeeFormValues } from '@/types';
import { ContractorCertificationData } from '@/api/apis/resources';
import { OrganType } from '@/api/apis/case';
import { ORGAN_ICON } from '@/utils/helpers';

type CertificationDatesRowProps = {
  isReadOnly?: boolean;
};

export const CertificationDatesRow = memo(({ isReadOnly }: CertificationDatesRowProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();
  const [organType, contractorCertifications] = useWatch({
    control,
    name: ['organType', 'contractorCertifications'],
  });

  const isOrganType = !!organType?.length;

  const onCertificationDateChange = (organ: OrganType, date: string | null) => {
    if (!date) return;

    if (contractorCertifications?.length) {
      const certification = contractorCertifications.find((c) => c.organType === organ);
      setValue(
        'contractorCertifications',
        certification
          ? contractorCertifications.map((c: ContractorCertificationData) =>
              c.organType === organ ? { ...c, certificationDate: date } : c,
            )
          : [...contractorCertifications, { organType: organ, certificationDate: date }],
        { shouldValidate: true },
      );
    } else {
      setValue('contractorCertifications', [{ organType: organ, certificationDate: date }], {
        shouldValidate: true,
      });
    }
  };

  return (
    <ConditionalRender show={isOrganType}>
      <AdminFormRow title="Initial Certification" isRequired center={false}>
        {organType?.map((o) => {
          const OrganIcon = ORGAN_ICON[o];
          const certification = contractorCertifications?.find((c: ContractorCertificationData) => c.organType === o);
          return (
            <TmdxDatepicker
              aria-label="Select OR Date"
              key={o}
              label={null}
              className="first:mt-0 mt-5"
              value={certification?.certificationDate ? parseDate(certification.certificationDate) : null}
              onChange={(v) => onCertificationDateChange(o, v ? v.toString() : v)}
              startContent={<OrganIcon />}
              isDisabled={isReadOnly}
            />
          );
        })}
      </AdminFormRow>
    </ConditionalRender>
  );
});
